import { Suspense, useEffect, useMemo, useState } from 'react';
import { SMS_SIGNUP_HIDDEN } from '../lib/notifications';
import { Link } from 'react-router-dom';
import { emailVerificationWaived } from '../lib/demoStudio';
import StudioLoading from '../components/StudioLoading';
import PasswordField from '../components/PasswordField';
import { motion } from 'framer-motion';
import { firebaseStatus } from '../lib/firebase';
import { useAuth } from '../lib/auth-context';
import {
  resendVerificationEmail,
  sendResetEmail,
  signInWithEmail,
  signInWithGoogle,
  signOut,
  signUpWithEmail,
} from '../lib/auth';
import { markReload } from '../lib/reloadGuard';
// Same stale-chunk protection as every route in App.tsx - see
// hub/src/lib/lazyRoute.ts's doc comment. These two role-based homes are
// lazy-loaded exactly like a route, just chosen by role instead of a URL.
import { lazyRoute } from '../lib/lazyRoute';
import AddDancersCard from '../components/AddDancersCard';
import {
  DEFAULT_NOTIF_PREFS,
  roleLabelFor,
  completeMyNotifPrefsOnboarding,
  useUserDoc,
  type ManagerTitle,
  type NotifPrefs,
  type UserDoc,
  type UserRole,
} from '../lib/users';
import { isFohOrManager } from '../lib/roleCapabilities';
import { useUpcomingSubRequests } from '../lib/subs';
import { useStaffTileCounts } from '../lib/tileCounts';
import { useMyOpenTasksStillOpenable } from '../lib/bagItemTaskLive';
import {
  bulletinAwaitsMyAction,
  useActiveBulletins,
} from '../lib/bulletins';
import { classPostAckExempt, type ClassPostDoc } from '../lib/classPosts';
import { type KidColor } from '../lib/familyView';
import { db } from '../lib/firebase';
import {
  collection as firestoreCollection,
  onSnapshot as firestoreOnSnapshot,
  orderBy as firestoreOrderBy,
  query as firestoreQuery,
  where as firestoreWhere,
} from 'firebase/firestore';
import { NavTile } from '../components/NavTile';
import { ParentPortalButton } from '../components/ParentPortalButton';
import HomePaintMark from '../components/HomePaintMark';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { IconBadge } from '../components/IconBadge';
import { NotifBell } from '../components/NotifBell';
import { teacherCanChooseTitle } from '../lib/teacherTitle';
import { CountdownPills } from '../components/CountdownPills';
import { StaffCompEvalWeekPills } from '../components/StaffCompEvalWeekPills';
import { ThemeToggle } from '../lib/theme';
import { acceptMyInvite, findMyInvite, type InviteDoc } from '../lib/invites';
import { getAuth } from 'firebase/auth';
import {
  CompactHomeLogo,
  Hero,
  HomeGreeting,
  SectionRule,
} from './landing/HeroAndTasks';
import {
  cardStyle,
  containerStyle,
  homeFrameStyle,
  dividerLineStyle,
  dividerStyle,
  eyebrowStyle,
  inputStyle,
  linkBtnStyle,
  noteStyle,
  primaryBtnStyle,
  secondaryBtnStyle,
  statusFailStyle,
  statusOkStyle,
} from './landing/styles';
import { useDancerSession } from '../lib/dancer-session-context';
import WhatsNewCard from '../components/WhatsNewCard';
import { SignInBagGroupHomeCard } from './BagGroupReadOnly';

const DancerHome = lazyRoute(() => import('./landing/DancerHome'));

const MotionLink = motion.create(Link);

/**
 * Public landing page for the LDG Hub.
 *
 * Surfaces, in order:
 *   - Firebase wiring status (so a misconfigured env shows on the screen).
 *   - Auth state: signed out   Google + email/password forms.
 *   - "Check your email" verification card for signed-up-but-not-clicked.
 *   - For verified-signed-in users, branches on users/{uid} doc:
 *       loading          quiet "setting up your account" card
 *       status=pending   "waiting for Bobbie to approve" card
 *       status=active    Director welcome (or "you're in" for non-Director)
 *       status=rejected/inactive   polite "talk to Bobbie" card
 *
 * The actual Director dashboard + Family / staff home screens land in
 * later phases; for now the signed-in cards are placeholders that
 * confirm the gate is wired right.
 */

/** How many "Needs your attention" rows show before the expander. */
export const ATTENTION_VISIBLE = 5;

function BulletinsStrip() {
  // One slim line, never a stack of banner cards (Bobbie, 2026-07-31: on
  // desktop the banners pushed the actual app below the fold - "Remove the
  // banners, that should just be under messages"). Announcements live in
  // Messages and /bulletins; home only says how many are waiting.
  //
  // "Waiting" means waiting ON YOU, via the shared predicate: something you
  // have not handled AND are actually allowed to handle. The old local
  // !actedOn filter counted announcements the /bulletins page hides or
  // offers no button for, so the strip could name a thing nobody could
  // clear - read five times, never gone.
  const { user } = useAuth();
  const meUid = user?.uid;
  const { userDoc } = useUserDoc();
  const { bulletins, loading } = useActiveBulletins();

  if (loading) return null;
  const waiting = bulletins.filter((b) => bulletinAwaitsMyAction(b, meUid, userDoc?.role));
  if (waiting.length === 0) return null;

  return (
    <Link
      to="/messages"
      style={{
        width: '100%',
        maxWidth: 360,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 44,
        padding: '0 var(--space-3)',
        background: 'var(--amber-wash, #FFF7E0)',
        border: '1px solid var(--amber-border, #F1DFA6)',
        borderRadius: 'var(--radius-md)',
        textDecoration: 'none',
        color: 'var(--amber-text)',
        fontWeight: 800,
        fontSize: 13.5,
      }}
    >
      <Icon name="megaphone" size={16} />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {waiting.length === 1
          ? `1 announcement waiting: ${waiting[0].title}`
          : `${waiting.length} announcements waiting`}
      </span>
      <span aria-hidden="true">{'\u203A'}</span>
    </Link>
  );
}

function FirebaseStatusCard() {
  // All-clear: stay quiet. No need to tell families the plumbing works;
  // we only speak up when something needs Bobbie's attention.
  if (firebaseStatus.state === 'ready') {
    return null;
  }
  if (firebaseStatus.state === 'missing-env') {
    return (
      <div style={statusFailStyle}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
          <Icon name="no-entry" size={15} /> Hang tight, the studio&apos;s still getting ready.
        </span>
        <br />
        A few setup bits are missing, so sign-in is paused for a moment.
        Ask the studio team for help and they&apos;ll have you dancing in no time.
      </div>
    );
  }
  return (
    <div style={{ ...statusFailStyle, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, flexWrap: 'wrap' }}>
      <Icon name="no-entry" size={15} />
      <span>Oops. We tripped on our way in.</span>
      <Link to="/hub-check" style={{ color: 'var(--rose-deep)', textDecoration: 'underline' }}>
        Open Hub Check
      </Link>
    </div>
  );
}

/**
 * Maps Firebase Auth's machine error codes to plain-English messages.
 * Catch-all returns the raw message so we never silently swallow detail.
 */
function friendlyAuthError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err ?? '');
  const code =
    (err as { code?: string } | null)?.code ??
    message.match(/auth\/[a-z0-9-]+/)?.[0] ??
    '';
  const map: Record<string, string> = {
    'auth/email-already-in-use':
      'Looks like you already have a spot here! Try signing in instead.',
    'auth/invalid-email': 'Hmm, that email looks a little off. Mind double-checking it?',
    'auth/weak-password': 'Let\'s pick a stronger password, at least 6 characters, superstar.',
    'auth/wrong-password': 'That password didn\'t match. Give it another twirl or reset it.',
    'auth/user-not-found':
      'We don\'t see you on the roster yet. Tap "Create account" to join us!',
    'auth/invalid-credential':
      'That email or password didn\'t match. Check that the email matches exactly what Manage Users shows (.com vs .con is an easy typo), make sure the password has no extra space at the start or end, or ask a Director to tap "Reset password" on your row for a fresh starter password.',
    'auth/too-many-requests':
      'Whoa, lots of tries! Take a quick water break and try again in a few minutes.',
    'auth/network-request-failed':
      'Looks like the connection slipped. Check your wifi and give it another go.',
    'auth/unauthorized-domain':
      'This preview link is not approved for sign-in yet. Use ldg-hub-staging.netlify.app, or add this exact deploy-preview domain in Firebase Auth Authorized domains.',
    'auth/operation-not-allowed':
      'That sign-in option is not switched on yet. Ask the studio team to finish the provider setup.',
    'auth/account-exists-with-different-credential':
      'That email already has a Hub account. Sign in with the method you used before.',
    'auth/credential-already-in-use':
      'That sign-in method is already connected to another Hub account.',
    'auth/popup-blocked': 'Your browser hid the sign-in window. Allow pop-ups for this site and try again.',
    'auth/browser-storage-unavailable':
      'This browser could not finish the secure sign-in handoff. Open the Hub in Safari or Chrome, or sign in with your email and password below.',
    'auth/internal-error':
      'This browser could not finish sign-in. Open the Hub in Safari or Chrome, or use your email and password below.',
    // The redirect marker says Google returned without a usable result. Email
    // and password does not use that browser handoff, so it remains a safe way
    // in even if storage was cleared mid-sign-in.
    'auth/redirect-result-lost':
      'Your browser lost the last step of that sign-in, so it landed back here. Your account is safe. Open the Hub in Safari or Chrome, or sign in with your email and password below.',
  };
  if (code && map[code]) return map[code];
  return message;
}

// Exported for tests/harness/dancer-session-ended, which mounts this REAL
// card under the REAL DancerSessionProvider to prove the "sign-in ended"
// notice by what renders, not by what the source says.
export function EmailPasswordForm() {
  const { login: loginDancer, sessionNotice } = useDancerSession();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Dancer collection deliberately does NOT live on this card. #482 put it on
  // the waiting-for-approval screen one tap later, where an account already
  // exists to hang a claim on, and a second form here would ask the same
  // parent the same question twice.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signUpWithEmail(email, password);
      } else {
        const typedIdentifier = email.trim();
        if (typedIdentifier.includes('@')) {
          await signInWithEmail(typedIdentifier.toLowerCase(), password.trim());
        } else {
          // Dancer credentials go only to the server-owned dancer session.
          // Preserve the password exactly as the parent chose it.
          await loginDancer(typedIdentifier, password);
        }
      }
    } catch (e2) {
      setErr(friendlyAuthError(e2));
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    setErr(null);
    setNotice(null);
    if (!email.trim()) {
      setErr('Type your email above first, then tap "Forgot password?"');
      return;
    }
    if (!email.includes('@')) {
      setErr('Ask your parent to set a new dancer password from their account.');
      return;
    }
    setBusy(true);
    try {
      await sendResetEmail(email.trim());
      setNotice(
        `If an account uses ${email.trim()}, a reset link is on its way. Check your email.`
      );
    } catch (e2) {
      setErr(friendlyAuthError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card as="form" onSubmit={handleSubmit}>
      <div style={eyebrowStyle}>
        <Icon name="wave-hello" size={14} />
        {mode === 'signup' ? 'Create account' : 'Welcome back'}
      </div>
      {mode === 'signin' && sessionNotice && (
        <div
          role="status"
          style={{
            fontSize: 13,
            color: 'var(--info-text)',
            background: 'var(--info-bg)',
            border: '1px solid var(--vio-border)',
            borderRadius: 'var(--radius-md)',
            padding: '8px 10px',
          }}
        >
          {sessionNotice}
        </div>
      )}
      {/* autoCorrect/autoCapitalize/spellCheck off: iPhone keyboards
          "fix" made-up test addresses as you type (.con becomes .com),
          which sent Kim's sign-ins to an address that does not exist.
          type="email" alone does not reliably stop text replacement. */}
      <input
        type={mode === 'signup' ? 'email' : 'text'}
        autoComplete={mode === 'signup' ? 'email' : 'username'}
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        placeholder={mode === 'signup' ? 'Email' : 'Email or dancer username'}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        style={inputStyle}
        disabled={busy}
      />
      <PasswordField
        autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
        placeholder={
          mode === 'signup' ? 'Create a password (6+ chars)' : 'Password'
        }
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        minLength={6}
        style={inputStyle}
        disabled={busy}
      />
      <Button
        variant="primary"
        fullWidth
        type="submit"
        disabled={busy || !email || !password}
      >
        {busy
          ? mode === 'signup'
            ? 'Creating account...'
            : 'Signing in...'
          : mode === 'signup'
          ? 'Create account'
          : 'Sign in'}
      </Button>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        {mode === 'signup' ? 'Already have an account? ' : 'Need an account? '}
        <button
          type="button"
          style={linkBtnStyle}
          onClick={() => {
            setErr(null);
            setNotice(null);
            setMode((m) => (m === 'signup' ? 'signin' : 'signup'));
          }}
        >
          {mode === 'signup' ? 'Sign in' : 'Create one'}
        </button>
      </div>
      {mode === 'signin' && (
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          Forgot your password?{' '}
          <button
            type="button"
            style={linkBtnStyle}
            disabled={busy}
            onClick={handleForgot}
          >
            Email me a reset link
          </button>
        </div>
      )}
      {notice && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--success-text)',
            background: 'var(--lime-wash)',
            border: '1px solid var(--lime)',
            borderRadius: 'var(--radius-md)',
            padding: '8px 10px',
          }}
        >
          {notice}
        </div>
      )}
      {err && <div style={statusFailStyle}>{err}</div>}
    </Card>
  );
}

function GoogleSignInButton() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleClick = async () => {
    setErr(null);
    setBusy(true);
    try {
      const user = await signInWithGoogle();
      // On mobile, signInWithGoogle returns null and the page is mid-redirect
      // to Google. Leave busy true because the browser is about to navigate.
      if (user === null) return;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (!/cancelled-popup|popup-closed-by-user/i.test(msg)) {
        setErr(friendlyAuthError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 360 }}>
      <Button
        variant='primary'
        fullWidth
        type='button'
        disabled={busy}
        onClick={handleClick}
      >
        {busy ? 'Opening Google...' : 'Sign in with Google'}
      </Button>
      {err && (
        <div style={{ ...statusFailStyle, marginTop: 'var(--space-2)' }}>
          {err}
        </div>
      )}
    </div>
  );
}

function OAuthRedirectError() {
  const { redirectError } = useAuth();
  if (!redirectError) return null;
  return (
    <div style={{ ...statusFailStyle, marginTop: 'var(--space-2)' }}>
      {friendlyAuthError({ message: redirectError })}
    </div>
  );
}

function VerifyEmailCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!user) return null;

  const handleResend = async () => {
    setMsg(null);
    setErr(null);
    setBusy(true);
    try {
      await resendVerificationEmail();
      setMsg('Sent. Check your inbox (and spam, in case).');
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setMsg(null);
    setErr(null);
    setBusy(true);
    try {
      const fresh = await refresh();
      if (fresh && !fresh.emailVerified) {
        setMsg(
          "Still not verified. Make sure you clicked the link in the email."
        );
      }
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ color: 'var(--studio-green)' }}><Icon name="inbox" size={32} /></div>
      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)' }}>
        Check your email
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        We sent a verification link to <strong>{user.email}</strong>. Click it,
        then tap below.
      </div>
      <button
        type="button"
        style={primaryBtnStyle}
        disabled={busy}
        onClick={handleRefresh}
      >
        {busy ? 'Checking...' : 'I clicked the link'}
      </button>
      <button
        type="button"
        style={secondaryBtnStyle}
        disabled={busy}
        onClick={handleResend}
      >
        Didn't get it? Resend
      </button>
      <button
        type="button"
        style={{ ...secondaryBtnStyle, color: 'var(--ink-ghost)' }}
        disabled={busy}
        onClick={() => signOut()}
      >
        Use a different account
      </button>
      {msg && (
        <div style={{ ...statusOkStyle, fontFamily: 'inherit' }}>{msg}</div>
      )}
      {err && <div style={statusFailStyle}>{err}</div>}
    </div>
  );
}

export function SignOutButton() {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      style={secondaryBtnStyle}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await signOut();
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? 'Signing out...' : 'Sign out'}
    </button>
  );
}

function UserIdentityRow() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)' }}>
        {user.displayName ?? user.email ?? 'Signed in'}
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{user.email}</div>
      <Link to="/account" style={{ ...linkBtnStyle, fontSize: 13, padding: 0 }}>
        Account &amp; password
      </Link>
    </>
  );
}

/**
 * Compact staff identity header (task #75). Mirrors the condensed
 * family-home header (small bold name + small "Account" link) instead
 * of the old name/email/Account block plus a big role pill plus a
 * separate "Good to see you" greeting, so Talk to Studio Assistant and
 * What needs you sit near the top of the screen on every staff role
 * card (Director, Executive Teachers, Jr Staff, Admin).
 */
export function StaffIdentityHeader({ roleLabel }: { roleLabel: string }) {
  const { user } = useAuth();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
        <span
          style={{
            fontWeight: 800,
            fontSize: 14,
            color: 'var(--ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {user?.displayName ?? user?.email ?? 'Signed in'}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)', flexShrink: 0 }}>
          {roleLabel}
        </span>
      </div>
      {/* A full 44px tap target (CLAUDE.md floor); the 2026-10-04 role
          crawl measured this link 48x19. The underline stays on the word. */}
      <Link
        to="/account"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          minHeight: 'var(--tap-target)',
          padding: '0 var(--space-1)',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--ink-soft)',
          flexShrink: 0,
          textDecoration: 'none',
        }}
      >
        <span style={{ borderBottom: '1px solid var(--line)' }}>Account</span>
      </Link>
    </div>
  );
}

function AccountLoadingCard() {
  // 20s watchdog (stability audit, finding: this was the ONE loading state
  // with no escape hatch - a hung Firestore socket on a resumed phone sat
  // here forever, which is exactly the "Setting up your account" report).
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setStuck(true), 20000);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div style={cardStyle}>
      <div style={{ color: 'var(--amber-text)' }}><Icon name="clock" size={32} /></div>
      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)' }}>
        Setting up your account...
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        One sec while we sync with the studio database.
      </div>
      {stuck && (
        <>
          <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 700 }}>
            This is taking longer than it should. A reload usually fixes it.
          </div>
          <button
            type="button"
            style={{
              minHeight: 44,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--lime)',
              color: 'var(--primary-on)',
              fontWeight: 900,
              fontSize: 14,
              padding: '0 var(--space-4)',
              cursor: 'pointer',
            }}
            onClick={() => window.location.reload()}
          >
            Reload the page
          </button>
          <SignOutButton />
        </>
      )}
    </div>
  );
}

function AccountErrorCard({ message }: { message: string }) {
  return (
    <div style={cardStyle}>
      <div style={{ color: 'var(--amber-text)' }}><Icon name="warning" size={32} /></div>
      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)' }}>
        Could not load your account
      </div>
      <div style={{ ...statusFailStyle, fontFamily: 'inherit' }}>{message}</div>
      <SignOutButton />
    </div>
  );
}

/**
 * Compact one-line home link in the same card language as NavTile (task
 * #140): flat paper card, hairline border, lime-wash icon chip, bold label.
 * Used for the Director home's "See the app as..." grid and "More" list so
 * the bottom of the page matches the tiles above it.
 */
export function CompactHomeLink({
  href,
  icon,
  label,
  onClick,
}: {
  href: string;
  icon: string;
  label: string;
  onClick?: () => void;
}) {
  return (
    <MotionLink
      to={href}
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        minHeight: 56,
        padding: '0 var(--space-3)',
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 18,
        boxShadow: 'var(--shadow-md)',
        textDecoration: 'none',
        color: 'var(--ink)',
        fontWeight: 800,
        fontSize: 14,
        minWidth: 0,
      }}
    >
      <IconBadge
        name={icon}
        size={34}
        radius={11}
        style={{
          background: 'var(--lime-wash)',
          border: '1px solid transparent',
          boxShadow: 'none',
          color: 'var(--lime-deep)',
          flexShrink: 0,
        }}
      />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </MotionLink>
  );
}

function PendingApprovalCard({ userDoc }: { userDoc: UserDoc }) {
  const [invite, setInvite] = useState<InviteDoc | null | 'loading'>('loading');
  const [accepting, setAccepting] = useState(false);
  const [acceptErr, setAcceptErr] = useState<string | null>(null);

  useEffect(() => {
    findMyInvite()
      .then((inv) => setInvite(inv))
      .catch(() => setInvite(null));
  }, []);

  const onAccept = async () => {
    setAccepting(true);
    setAcceptErr(null);
    try {
      const res = await acceptMyInvite();
      if (res.ok) {
        // Force token refresh so the new custom claim is picked up.
        await getAuth().currentUser?.getIdToken(true);
        // Shared reload cooldown (see reloadGuard.ts): this deliberate
        // reload must not be chased by an automatic freshness reload a
        // moment after the fresh page mounts.
        markReload();
        window.location.reload();
      } else {
        setAcceptErr(res.reason ?? 'Something went wrong. Ask the studio team to confirm.');
      }
    } catch (e) {
      setAcceptErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAccepting(false);
    }
  };

  if (invite && invite !== 'loading') {
    // Pre-approved via invite - show the fast-track card.
    const roleLabel = ({
      company: 'Executive Teachers',
      ensemble: 'Jr Staff',
      foh: 'Admin',
      family: 'Family member',
      director: 'Director',
    } as Record<string, string>)[invite.role] ?? invite.role;
    return (
      <div style={cardStyle}>
        <span style={{ alignSelf: 'center', color: 'var(--studio-green)' }}><Icon name="star" size={32} /></span>
        <UserIdentityRow />
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>
          You&apos;ve been pre-approved!
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          The Studio LDG team has set you up as <strong>{roleLabel}</strong> at The Studio LDG Hub.
          Tap below to accept your invitation and get started.
        </div>
        <button
          type="button"
          style={primaryBtnStyle}
          disabled={accepting}
          onClick={onAccept}
        >
          {accepting ? 'Joining the Hub...' : 'Accept invitation'}
        </button>
        {acceptErr && (
          <div style={{ fontSize: 12, color: 'var(--rose-deep)' }}>{acceptErr}</div>
        )}
        <SignOutButton />
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ color: 'var(--amber-text)' }}><Icon name="clock" size={32} /></div>
      <UserIdentityRow />
      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>
        Waiting for studio approval
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        Your account is set up but not active yet. While you wait, add your
        dancers below so the studio can connect them the moment they approve you.
      </div>
      {/* ONE dancer form, not two. The old #482 singular form used to sit
          right here above this card, so a phone showed two parent-name fields,
          two dancer forms and two save buttons at once. AddDancersCard is now
          the only place a parent is asked, on every path, and it scales from
          one dancer to twelve. Any legacy singular claim folds in read-only. */}
      <AddDancersCard
        displayName={userDoc.displayName}
        intro="Add every dancer in your family, rec classes and comp team both. The studio connects them to your account the moment they approve you."
        compact
      />
      <SignOutButton />
    </div>
  );
}

function RejectedCard() {
  return (
    <div style={cardStyle}>
      <div style={{ color: 'var(--amber-text)' }}><Icon name="warning" size={32} /></div>
      <UserIdentityRow />
      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>
        Account not approved
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        This account isn&apos;t connected to a studio family. If you think
        that&apos;s a mistake, ask the studio team and they&apos;ll sort it out.
      </div>
      <SignOutButton />
    </div>
  );
}

function InactiveCard() {
  return (
    <div style={cardStyle}>
      <div style={{ color: 'var(--studio-green)' }}><Icon name="moon" size={32} /></div>
      <UserIdentityRow />
      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>
        Account paused
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        This account is currently inactive. Ask the studio team if you need it
        reactivated.
      </div>
      <SignOutButton />
    </div>
  );
}

const prefRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--space-3) var(--space-4)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--white)',
  cursor: 'pointer',
  marginBottom: 8,
};

const prefRowActiveStyle: React.CSSProperties = {
  ...prefRowStyle,
  borderColor: 'var(--lime)',
  background: 'var(--lime-wash)',
};

/**
 * Onboarding card - shown once, the first time a user lands on the
 * Hub after sign-up, before they've answered "how do you want to
 * hear from us?" Multi-select; "Skip for now" leaves existing choices alone
 * and marks a missing preference map complete, using per-key defaults.
 *
 * After the user has answered, this component returns null and the
 * existing role card takes over.
 */
type OnboardingChannel = 'app' | 'email' | 'sms';

function NotifPrefsOnboarding({ uid }: { uid: string }) {
  const [edits, setEdits] = useState<Partial<Pick<NotifPrefs, OnboardingChannel>>>({});
  const picks = { ...DEFAULT_NOTIF_PREFS, ...edits };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (key: OnboardingChannel) => {
    if (busy) return;
    setEdits((current) => {
      const next = !(current[key] ?? DEFAULT_NOTIF_PREFS[key]);
      const changed = { ...current };
      if (next === DEFAULT_NOTIF_PREFS[key]) delete changed[key];
      else changed[key] = next;
      return changed;
    });
  };

  const save = async (choices: Partial<Pick<NotifPrefs, OnboardingChannel>>) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await completeMyNotifPrefsOnboarding(uid, choices);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
    // success path: userDoc will refresh from the snapshot listener and
    // this component will unmount; no need to clear busy.
  };

  const rows: { key: OnboardingChannel; icon: string; title: string; body: string }[] = [
    {
      key: 'app',
      icon: 'bell',
      title: 'App notifications',
      body: 'Push popups on your phone (when this lands later in Phase 2A).',
    },
    {
      key: 'email',
      icon: 'inbox',
      title: 'Emails',
      body: 'Direct messages and a periodic digest.',
    },
    // Text sign-up hidden for now (SMS_SIGNUP_HIDDEN in lib/notifications.ts,
    // Bobbie 2026-08-19); the sms row returns when that flag flips.
    ...(SMS_SIGNUP_HIDDEN ? [] : [{
      key: 'sms' as const,
      icon: 'chat-bubble',
      title: 'Text messages',
      body: 'For the rare urgent thing. Off by default.',
    }]),
  ];

  return (
    <div style={{ ...cardStyle, maxWidth: 420, gap: 'var(--space-3)' }}>
      <span style={{ alignSelf: 'center', color: 'var(--studio-green)' }}><Icon name="wave-hello" size={30} /></span>
      <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--ink)' }}>
        How would you like to hear from us?
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        Pick everything you want. Don&apos;t worry, you can change
        these in your settings later.
      </div>

      <div style={{ textAlign: 'left' }}>
        {rows.map((r) => {
          const on = picks[r.key];
          return (
            <button
              type="button"
              key={r.key}
              style={{ ...(on ? prefRowActiveStyle : prefRowStyle), width: '100%', font: 'inherit', textAlign: 'left' }}
              onClick={() => toggle(r.key)}
              disabled={busy}
              role="checkbox"
              aria-checked={on}
            >
              <div>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 14,
                    color: 'var(--ink)',
                  }}
                >
                  <span style={{ marginRight: 6 }}><Icon name={r.icon} size={20} /></span>
                  {r.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                  {r.body}
                </div>
              </div>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: on ? 'var(--lime)' : 'var(--white)',
                  border: '2px solid',
                  borderColor: on ? 'var(--lime)' : 'var(--line)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--primary-on)',
                  fontWeight: 800,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {on ? (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 6,
                      borderLeft: '2px solid var(--primary-on)',
                      borderBottom: '2px solid var(--primary-on)',
                      transform: 'rotate(-45deg)',
                      marginTop: -2,
                    }}
                  />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        style={{
          ...primaryBtnStyle,
          opacity: busy ? 0.6 : 1,
        }}
        disabled={busy}
        onClick={() => save(edits)}
      >
        {busy ? 'Saving...' : 'Save preferences'}
      </button>
      <button
        type="button"
        style={{
          ...secondaryBtnStyle,
          color: 'var(--ink-ghost)',
        }}
        disabled={busy}
        onClick={() => save({})}
      >
        Skip for now (use defaults)
      </button>
      {err && <div style={{ ...statusFailStyle, fontFamily: 'inherit' }}>{err}</div>}
    </div>
  );
}

export const attentionCardStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--white)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

export const attentionRowLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: '1px solid var(--line)',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
};

export const attentionLineStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--ink)',
};

/* The Studio board NavTile retired with the /board page itself - staff
 * announcements live in the merged /messages list now, so the Messages tile
 * carries the outstanding-announcement count in its waiting badge. */

/**
 * Staff home content, split out of this file into its own lazy chunk (perf
 * round, packet 2026-08-19 "home screen first paint"). See
 * ./landing/StaffHomeContent.tsx's own header comment for the full why and
 * the four traps it honors. DirectorCard is the whole Director home card,
 * swapped in exactly like AdminHome below; the other six are pieces
 * ActiveMemberCard composes inline for the manager/company/ensemble home,
 * all under ONE shared Suspense boundary (same chunk, so they resolve
 * together) - see its return below. Every dynamic import() here points at
 * the same module specifier, so React/Vite fetch it once no matter how many
 * of these bindings a caller renders.
 */
const DirectorCard = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.DirectorCard }))
);
const StaffAttentionCard = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.StaffAttentionCard }))
);
const TonightWidget = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.TonightWidget }))
);
const HomeTicker = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.HomeTicker }))
);
const AssistantCallout = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.AssistantCallout }))
);
const MyDancersSection = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.MyDancersSection }))
);
const JrStaffClassesFirst = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.JrStaffClassesFirst }))
);
const YourClassesSection = lazyRoute(() =>
  import('./landing/StaffHomeContent').then((m) => ({ default: m.YourClassesSection }))
);

/**
 * Color-coded chip for a dancer, used in the family home + (future)
 * calendar legend. Tap to drill into the dancer's profile (next PR).
 */
export function DancerChip({
  name,
  color,
  studentId,
  compact = false,
}: {
  name: string;
  color: KidColor;
  studentId: string;
  /** Tighter padding/type for the condensed family home header (task #54). */
  compact?: boolean;
}) {
  return (
    <Link
      to={`/family/dancer/${studentId}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 5 : 6,
        background: color.bg,
        color: color.fg,
        border: `2px solid ${color.border}`,
        borderRadius: 'var(--radius-pill)',
        padding: compact ? '5px 10px' : '6px 14px',
        fontSize: compact ? 12 : 14,
        fontWeight: 800,
        margin: compact ? '3px 4px 3px 0' : '4px 4px 4px 0',
        textDecoration: 'none',
        minHeight: compact ? 32 : 36,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: compact ? 8 : 10,
          height: compact ? 8 : 10,
          borderRadius: '50%',
          background: color.border,
        }}
      />
      {name}
    </Link>
  );
}

const bigButtonStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  padding: 'var(--space-3) var(--space-2)',
  background: 'var(--white)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  textDecoration: 'none',
  color: 'var(--ink)',
  minHeight: 64,
  fontWeight: 800,
  fontSize: 13,
};

export const bigButtonIconStyle: React.CSSProperties = {
  fontSize: 28,
  lineHeight: 1,
};

/**
 * Family home tiles (#191) - the parent grid is now just five doors, so each
 * one gets to be bigger and easier to hit with a thumb than the standard
 * bigButtonStyle tile.
 */
export const familyBigButtonStyle: React.CSSProperties = {
  ...bigButtonStyle,
  minHeight: 92,
  gap: 6,
  fontSize: 14,
  textAlign: 'center',
};

/**
 * The mockup's section label: 11px, weight 800, tracked hard, in the readable
 * green rather than grey. Letter-spacing is 2.4px, which is the mockup's .22em
 * at this size; em would re-derive from whatever font-size a caller passes.
 *
 * --lime-deep, never --studio-green: see the note at the top of theme.css.
 */
export const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 800,
  color: 'var(--lime-deep)',
  textTransform: 'uppercase',
  letterSpacing: 2.4,
  alignSelf: 'flex-start',
  marginTop: 'var(--space-3)',
  marginBottom: 4,
};

/**
 * Class-family posts that need this parent's acknowledgement, with the full
 * post body (not just a count) so the "Needs your attention" accordion can
 * expand right there instead of sending a parent to another page.
 */
export function useMyOutstandingClassPostItems(): {
  loading: boolean;
  posts: ClassPostDoc[];
} {
  const { user } = useAuth();
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db || !user) {
      setPosts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = firestoreQuery(
      firestoreCollection(db, 'classPosts'),
      firestoreWhere('expectedRecipientUids', 'array-contains', user.uid),
      firestoreOrderBy('createdAt', 'desc')
    );
    const unsub = firestoreOnSnapshot(
      q,
      (snap) => {
        setPosts(
          snap.docs.map(
            (d) => ({ id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) })
          )
        );
        setLoading(false);
      },
      () => {
        setPosts([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user]);

  const outstanding = useMemo(() => {
    if (!user) return [];
    return posts.filter(
      (p) =>
        p.ackRequired &&
        // Readable, not owed: a post the enrollment repair handed her after
        // the fact never asks for her acknowledgement. See classPostAckExempt.
        !classPostAckExempt(p, user.uid) &&
        !(p.acks && p.acks[user.uid]) &&
        !(p.dismissedBy && p.dismissedBy[user.uid])
    );
  }, [posts, user]);

  return { loading, posts: outstanding };
}

export const attentionAccordionButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minHeight: 44,
  padding: 'var(--space-2) var(--space-3)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'var(--font-body)',
};

export const attentionPrimaryButtonStyle: React.CSSProperties = {
  background: 'var(--lime)',
  color: 'var(--primary-on)',
  fontWeight: 800,
  fontSize: 13,
  padding: '8px 14px',
  minHeight: 44,
  border: 0,
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

export const attentionSecondaryButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-soft)',
  border: '1px solid var(--line)',
  fontWeight: 700,
  fontSize: 13,
  padding: '8px 14px',
  minHeight: 44,
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

export const attentionLinkButtonStyle: React.CSSProperties = {
  ...attentionSecondaryButtonStyle,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
};

/**
 * Role-split lazy homes (perf round, audit #3). The foh AdminHome card
 * lives in its own chunk under ./landing/, so a family or staff account
 * never parses the admin card. Landing itself stays eager.
 *
 * FamilyCommandCenter (../FamilyCommandCenter) IS the family home now - the
 * old FamilyHome.tsx this used to render is deleted, its snooze helpers
 * moved to lib/attentionSnooze.ts, and everything else it showed was folded
 * into FamilyCommandCenter (see that file's own capability audit). Landing()
 * mounts it DIRECTLY, bypassing this component and this file's own
 * containerStyle/top-bar chrome entirely, for family accounts and the Admin
 * default home. Admin View remains a separate menu destination with the
 * same account and permissions. For the full-page gate,
 * see its isResolvedActiveFamily branch below. ActiveMemberCard's own
 * family-home branch further down should therefore be unreachable;
 * it is kept as a safety net, not the primary path.
 */
const AdminHome = lazyRoute(() => import('./landing/AdminHome'));
/** The family home itself - see Landing()'s isResolvedActiveFamily branch below. */
const FamilyCommandCenter = lazyRoute(() => import('./FamilyCommandCenter'));

export function TeacherTitleHomePrompt() {
  const { realUserDoc } = useUserDoc();
  const needsChoice =
    teacherCanChooseTitle(realUserDoc) &&
    !realUserDoc?.teacherTitle;
  if (!needsChoice) return null;
  return (
    <Link
      to="/account"
      style={{
        minHeight: 56,
        padding: '12px 14px',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--lime)',
        background: 'var(--lime-wash)',
        color: 'var(--ink)',
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <Icon name="people" size={22} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <strong style={{ display: 'block', fontSize: 15 }}>Choose Ms or Coach</strong>
        <span style={{ display: 'block', marginTop: 2, fontSize: 13, color: 'var(--ink-soft)' }}>
          Set the one teacher name families and dancers will see everywhere.
        </span>
      </span>
      <span aria-hidden="true" style={{ fontSize: 20 }}>&gt;</span>
    </Link>
  );
}

function ActiveMemberCard({
  role,
  managerTitle,
  adminView = false,
}: {
  role: string;
  managerTitle?: ManagerTitle | null;
  adminView?: boolean;
}) {
  if (role === 'family' || (role === 'foh' && !adminView)) {
    // Should be unreachable in practice: Landing()'s isResolvedActiveFamily
    // check mounts FamilyCommandCenter directly (also for Admin's default), before this component is
    // ever called, for every gate this branch would also need to pass. Kept
    // anyway as a safety net - the two checks are independent code that a
    // later edit could silently de-sync, and the failure mode without this
    // branch is not a crash, it is a family account seeing an almost-empty
    // staff-shaped card (the isStaff-gated content below all skips a
    // non-staff role) with no obvious sign anything is wrong.
    return (
      <Suspense fallback={<StudioLoading fullPage label="Setting up your home" />}>
        <HomePaintMark />
        <FamilyCommandCenter showThemeToggle />
      </Suspense>
    );
  }
  if (role === 'foh') {
    return (
      <Suspense fallback={<StudioLoading fullPage label="Setting up your home" />}>
        <HomePaintMark />
        <AdminHome />
      </Suspense>
    );
  }
  // The staff card is its own COMPONENT, not inlined here, and that is a
  // crash fix: its four listener hooks used to run below the two early
  // returns above, so a role changing on a mounted card (Director's role
  // preview switching staff to foh or family, or a live role edit) changed
  // the hook count mid-flight - React error #310, the home page replaced by
  // the error boundary. Rendering a child conditionally is fine; calling
  // hooks conditionally is not. The split keeps the other point of the early
  // returns intact too: family and foh still never open the staff-only
  // listeners those hooks hold.
  return <StaffMemberHome role={role} managerTitle={managerTitle} />;
}

function StaffMemberHome({
  role,
  managerTitle,
}: {
  role: string;
  managerTitle?: ManagerTitle | null;
}) {
  const isStaff =
    role === 'manager' || role === 'company' || role === 'ensemble' || role === 'foh';
  // Perf round (home-listener-dedupe): lifted once here instead of once
  // each inside HomeTicker, StaffAttentionCard, TonightWidget's scoped
  // branch and useStaffTileCounts - see DirectorCard's identical comment.
  // isStaff is always true by the time this line runs (family/foh both
  // returned above), so this is provable for every role that reaches here.
  const { tasks: openTasks, loading: tasksLoading, confirmed: tasksConfirmed } = useMyOpenTasksStillOpenable(99);
  const {
    requests: subs,
    loading: subsLoading,
    error: subsError,
    fromCache: subsFromCache,
  } = useUpcomingSubRequests(true, true);
  // App-style number badges for the staff tile grid, fed by the lifted
  // tasks/subs above instead of opening its own copies.
  const tileCounts = useStaffTileCounts({ openTasks, tasksLoading, subs, subsLoading, subsError });
  // The seven pieces below (HomeTicker through YourClassesSection/
  // TonightWidget/MyDancersSection) all come from one lazy chunk -
  // ./landing/StaffHomeContent.tsx, see its header comment. ONE Suspense
  // boundary around the whole card, not one per piece: they resolve
  // together (same chunk, same promise) and StudioLoading is the fallback
  // here exactly as AdminHome/DirectorCard's own wrapper does, never null.
  return (
    <Suspense fallback={<StudioLoading fullPage label="Setting up your home" />}>
      <HomePaintMark />
      <div style={homeFrameStyle}>
        {isStaff && <HomeTicker subs={subs} />}
        <StaffIdentityHeader roleLabel={roleLabelFor({ role: role as UserRole, managerTitle })} />
        {/* The greeting carries the role, so the separate "Welcome back" line
            and the lone centred star above it are no longer saying anything the
            headline does not already say. */}
        <HomeGreeting roleLabel={roleLabelFor({ role: role as UserRole, managerTitle })} />
        {/* Her classes on the first screen (Bobbie, 2026-10-05, "Yes, under
            the greeting"; DECISIONS 121), the same move as DirectorCard's: the
            Teach row used to sit below the attention card, a screen or more
            down a phone. Everything below moves down one row.

            A JR STAFF opens on her own blended My classes row FIRST (Bobbie,
            2026-09-07: *"it should all be blended in with her classes ...
            That should be true for all junior staff."*), the same row her
            dancer sign-in opens on, then the Teach row, both counted over
            every uid she holds (JrStaffClassesFirst). Only the ensemble role:
            an Executive teacher's classes are all teaching classes and the
            Teach page is already that list, so she keeps the Teach row alone. */}
        {isStaff && (role === 'ensemble' ? <JrStaffClassesFirst subs={subs} /> : <YourClassesSection />)}
        {/* HER BAG POSTS ON HER HOME, on this sign-in too (DECISIONS 37 rule
            2, packet BG-10). Draws nothing unless this Jr Staff sign-in is
            the dancer herself and her dancer is in a bag group. */}
        {role === 'ensemble' && <SignInBagGroupHomeCard />}
        <TeacherTitleHomePrompt />
        {/* What's new for this role (DECISIONS 120), below the classes row
            (DECISIONS 121). */}
        {isStaff && <WhatsNewCard role={role as UserRole} />}
        <CountdownPills role={role} />
        {isStaff && <StaffCompEvalWeekPills />}
        {isStaff && <AssistantCallout />}
        {isStaff && (
          <StaffAttentionCard
            openTasks={openTasks}
            tasksConfirmed={tasksConfirmed}
            subs={subs}
            subsLoading={subsLoading}
            subsFromCache={subsFromCache}
            waiting={tileCounts.messageWaiting}
          />
        )}
        {isStaff && (
          <>
            {/* Only company (Executive teacher) and ensemble (Junior staff)
                reach this branch - foh returned early above, and Director
                has its own DirectorCard. Both get the scoped view: Bobbie
                asked for Executive teachers to see only their own classes
                here, and the same reasoning applies to Junior staff, who
                already see less than the full studio everywhere else. */}
            <TonightWidget
              scoped={role !== 'manager'}
              subs={subs}
              subsLoading={subsLoading}
              subsError={subsError}
              subsFromCache={subsFromCache}
            />
            {/* Bobbie: "Same thing I am a teacher and I have my own students."
                After Tonight, before Everywhere else, matching the Director
                home's order. Renders nothing at all for a staff account with
                no dancers, which is nearly all of them - see
                MyDancersSection. */}
            <MyDancersSection />
            <SectionRule label="Everywhere else" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-3)',
              }}
            >
                  <NavTile href="/assistant" icon="mic" label="Assistant" />
              <NavTile
                href="/messages"
                icon="chat-bubble"
                label="Messages"
                waiting={tileCounts.messages}
                waitingIncomplete={tileCounts.messagesFailed}
              />
              <NavTile href="/tasks" icon="clipboard" label="To-dos" waiting={tileCounts.tasks} />
              {/* Studio board tile folded into Messages - staff announcements
                  now live in the merged /messages list and count into its
                  badge. */}
              <NavTile href="/teach" icon="graduation-cap" label="Teach" />
              {/* "My classes" added in the Rehearsals tile's old spot, then
                  Calendar right after it (task #77, Bobbie: "remove
                  rehearsals from the teacher view ... just add a my classes
                  link ... and the calendar link should be where rehearsals
                  was added"). Rehearsals stays a real page for Director/Admin,
                  reachable from the hamburger drawer - it is just not a
                  teacher quick-nav tile anymore. */}
              {/* My classes is back on every staff home (Bobbie, 2026-10-18,
                  DECISIONS 131, circling this grid: "we should probably add a
                  button at the bottom that says my classes"). It reverses her
                  earlier "my classes can be removed from teachers and
                  director": Teach is the only list of a teacher's classes, so
                  it gets an obvious second door (a class missing from that
                  list is fixed in Teach itself, #1381). It opens Teach on its
                  class list (/teach/classes redirects to #all-classes). */}
              <NavTile href="/teach/classes" icon="graduation-cap" label="My classes" />
              <NavTile href="/calendar" icon="calendar" label="Calendar" />
              <NavTile href="/growth" icon="growth" label="Student evaluation" />
              <NavTile href="/subs" icon="people" label="Sub board" waiting={tileCounts.subs} />
              <NavTile href="/student-awards" icon="star" label="Student Awards" waiting={tileCounts.awards} />
              {role !== 'manager' && (
                <NavTile href="/production-tracker" icon="music" label="Recital/Comp Tracker" />
              )}
              {role === 'manager' && (
                <>
                  <NavTile href="/students" icon="people" label="All dancers" />
                  <NavTile href="/attendance/report" icon="chart" label="Attendance report" />
                  <NavTile href="/injuries" icon="clipboard" label="Injuries" />
                  <NavTile href="/bulletins" icon="megaphone" label="Message studio" />
                </>
              )}
              {/* Bag Groups tile dropped here too (task #73) - this branch was
                  already unreachable (role is never 'foh' by the time this
                  block renders; see the early foh return above), but it read
                  as though Jr/Executive staff might see it, so it is gone
                  rather than left as confusing dead code. */}
            </div>
            {/* Same compact-link card language as the Director home's "More"
                list (icon-badge, hairline card), not the old flat text-only
                row - the two staff home cards had drifted out of parity. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
                More
              </div>
              {(
                [
                  ['/attendance', 'Attendance', 'check'],
                  ['/bulletins', 'Announcements', 'megaphone'],
                  ...(isFohOrManager(role) ? [['/offerings', 'Classes', 'graduation-cap']] as [string, string, string][] : []),
                  // A manager holds every front-desk power as of the 2026-08-13
                  // union ruling, and these were the destinations it held with
                  // no door on this screen. Deliberately ADDED to the staff home
                  // rather than swapping a manager onto AdminHome: AdminHome
                  // carries no teaching content, so the swap would have taken
                  // away the Executive Teacher half of the role to give it the
                  // Admin half, which is not what a union means.
                  ...(role === 'manager'
                    ? ([
                        ['/registrations', 'Registrations', 'clipboard'],
                        ['/bag-groups', 'Bag Groups', 'people'],
                      ] as [string, string, string][])
                    : []),
                ] as [string, string, string][]
              ).map(([href, label, icon]) => (
                <CompactHomeLink key={href} href={href} icon={icon} label={label} />
              ))}
              <ParentPortalButton />
            </div>
          </>
        )}
        <SignOutButton />
      </div>
    </Suspense>
  );
}

/**
 * Exported (only) so the guide-shot harness's role-smoke screens
 * (tests/harness/guide-shots/screens.tsx, 'smoke-home-<role>') can mount the
 * real DirectorCard/ActiveMemberCard branch straight from Landing.tsx's own
 * lazy wiring - the exact code path a signed-in role actually renders -
 * without also pulling in Landing()'s dancer-session and family-redirect
 * branches, which the guide-shot mocks do not model per-role. Not used by
 * App.tsx or any other production call site; Landing itself still calls it
 * unqualified, same as before.
 */
export function VerifiedSignedInBlock({ adminView = false }: { adminView?: boolean }) {
  const account = useUserDoc();
  return <ResolvedSignedInBlock account={account} adminView={adminView} />;
}

// Home selection and its fallback must use the same account snapshot. A
// second listener could be healthy while the first has failed or is loading.
function ResolvedSignedInBlock({ account, adminView = false }: {
  account: Pick<ReturnType<typeof useUserDoc>, 'loading' | 'userDoc' | 'error'>;
  adminView?: boolean;
}) {
  const { loading, userDoc, error } = account;

  if (error) return <AccountErrorCard message={error} />;
  if (loading || !userDoc) return <AccountLoadingCard />;

  if (userDoc.status === 'pending') return <PendingApprovalCard userDoc={userDoc} />;
  if (userDoc.status === 'rejected') return <RejectedCard />;
  if (userDoc.status === 'inactive') return <InactiveCard />;

  // The forced temp-password change gate lives in GlobalChrome's
  // ForcePasswordOverlay now (claim-based, covers EVERY route, dismissible
  // after a successful change) - no Landing-only branch needed.

  // status === 'active'
  // First-time gate: ask how they want to hear from us. The card saves
  // either the user's picks or the defaults (via "Skip for now") and
  // then this branch resolves to the normal role card on the next
  // userDoc snapshot.
  if (userDoc.notifPrefs === undefined) {
    return <NotifPrefsOnboarding key={userDoc.uid} uid={userDoc.uid} />;
  }

  if (userDoc.role === 'director') {
    return (
      <Suspense fallback={<StudioLoading fullPage label="Setting up your home" />}>
        <HomePaintMark />
        <DirectorCard />
      </Suspense>
    );
  }
  return <ActiveMemberCard role={userDoc.role} managerTitle={userDoc.managerTitle} adminView={adminView} />;
}

function SignedOutBlock() {
  return (
    <>
      <GoogleSignInButton />
      <OAuthRedirectError />
      <div style={dividerStyle}>
        <div style={dividerLineStyle} />
        <span>or</span>
        <div style={dividerLineStyle} />
      </div>
      <EmailPasswordForm />
    </>
  );
}

function Landing({ adminView = false }: { adminView?: boolean }) {
  const { user, loading: authLoading } = useAuth();
  const account = useUserDoc();
  const { userDoc, loading: meLoading, error: meError } = account;
  const { authenticated: dancerAuthenticated } = useDancerSession();

  // Deliberately `dancerAuthenticated`, NOT the child-only `dancerOnly` test
  // App.tsx uses for routing (Jr Staff parity, 2026-08-21). A promoted Jr
  // Staff dancer gains the whole staff app - every staff route, the nav, the
  // chrome - but HOME stays the dancer home they already know, with the
  // classes she teaches blended into her own My classes list on it. Parity is a surface gained, never
  // their own surface swapped out from under a kid. The staff app is one tap
  // away in the nav; if Bobbie wants the reverse default, this one line is
  // the switch.
  if (dancerAuthenticated) {
    return (
      <Suspense fallback={<StudioLoading fullPage label="Getting your dance day ready..." />}>
        <HomePaintMark />
        <DancerHome />
      </Suspense>
    );
  }

  // Family and Admin accounts: the Family Command Center IS home (Bobbie: "the
  // home page for families should be the command center it is not updated
  // yet!"), not a card nested inside Landing's own auth-page chrome. It has
  // to render as its OWN full page - matching /family/command-center -
  // rather than nested inside containerStyle below, whose textAlign:center
  // and centered-flex sizing would fight its left-aligned poster design
  // (see FamilyCommandCenter's own pageStyle comment). Every other gate
  // (loading, email verification, pending/rejected/inactive, notifPrefs
  // onboarding) still has to resolve first, so this only fires once the
  // signed-in account is POSITIVELY known to be an active family account -
  // Admin reaches its staff dashboard only from /admin-view, not a saved
  // preference. Home therefore always returns to the family's own page.
  // Anything else (including "still loading") falls through to the normal
  // return below, which still has its own family branch as a fallback via
  // VerifiedSignedInBlock for any edge case this check does not cover.
  const isResolvedActiveFamily =
    !!user &&
    !authLoading &&
    !meLoading &&
    !meError &&
    (user.emailVerified || emailVerificationWaived()) &&
    !!userDoc &&
    userDoc.status === 'active' &&
    (userDoc.role === 'family' || (userDoc.role === 'foh' && !adminView)) &&
    userDoc.notifPrefs !== undefined;

  if (isResolvedActiveFamily) {
    // showThemeToggle: FamilyCommandCenter's own dark header already carries
    // a wordmark, a notification bell, and an Account button, so it renders
    // ONE integrated theme toggle button there rather than this file adding
    // a second, separate one above it. GlobalChrome's own compact ThemeChip
    // deliberately hides itself on "/", trusting whatever renders here to
    // supply the real toggle (see BreadcrumbStrip's onHome branch in
    // GlobalChrome.tsx) - so without showThemeToggle a family account would
    // have no way to switch light/dark mode at all.
    return (
      <Suspense fallback={<StudioLoading fullPage label="Setting up your home" />}>
        <HomePaintMark />
        <FamilyCommandCenter showThemeToggle />
      </Suspense>
    );
  }

  const renderAuthState = () => {
    if (authLoading) {
      {/* fullPage so the 20s watchdog shows its Reload button here too -
          this loader had the "taking longer than it should" text with no
          way out (stability audit F1). */}
      return <StudioLoading fullPage label="Choreographing the dance..." />;
    }
    // TEMPORARY testing waiver - see emailVerificationWaived(). On approved
    // testing hosts an unverified session proceeds; approval in Manage
    // Users (status active) remains the real gate.
    if (user && !user.emailVerified && !emailVerificationWaived()) {
      return <VerifyEmailCard />;
    }
    if (user) {
      return <ResolvedSignedInBlock account={account} adminView={adminView} />;
    }
    if (firebaseStatus.state === 'ready') {
      return <SignedOutBlock />;
    }
    return null;
  };

  return (
    <div style={containerStyle}>
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: user ? 'space-between' : 'flex-end',
          gap: 'var(--space-2)',
        }}
      >
        {user && <CompactHomeLogo />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {user && <NotifBell />}
          <ThemeToggle />
        </div>
      </div>
      {!user && <Hero />}

      <FirebaseStatusCard />

      {user && userDoc?.status === 'active' && <BulletinsStrip />}

      {renderAuthState()}

      {firebaseStatus.state !== 'ready' && (
        <p style={noteStyle}>
          Setup is paused on this device. The studio team can finish the Hub setup from Hub Check.
        </p>
      )}
    </div>
  );
}

export default Landing;
